import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { AlertCircle, CheckCircle2, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SetupAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState(null);
  
  const [invitation, setInvitation] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirm_password: '',
    alternate_email: '',
    phone_number: ''
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setValidating(false);
      setLoading(false);
      return;
    }
    
    validateInvitation();
  }, [token]);

  const validateInvitation = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/invitations/validate/${token}`);
      setInvitation(response.data);
      setFormData(prev => ({
        ...prev,
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        phone_number: response.data.phone_number || ''
      }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired invitation link');
    } finally {
      setValidating(false);
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.last_name.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!formData.phone_number || !formData.phone_number.trim()) {
      errors.phone_number = 'Phone number is required';
    } else {
      // Validate phone number has at least 10 digits
      const phoneDigits = formData.phone_number.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        errors.phone_number = 'Please enter a valid phone number with at least 10 digits';
      }
    }
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirm_password) {
      errors.confirm_password = 'Passwords do not match';
    }
    if (!formData.alternate_email) {
      errors.alternate_email = 'Alternate email is required for MFA';
    } else if (!/\S+@\S+\.\S+/.test(formData.alternate_email)) {
      errors.alternate_email = 'Please enter a valid email address';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API_URL}/api/invitations/complete`, {
        invitation_token: token,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        alternate_email: formData.alternate_email,
        phone_number: formData.phone_number
      });
      
      setCompleted(true);
      toast.success('Profile setup complete!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete profile setup');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-teal-600" />
            <p className="mt-4 text-slate-500">Validating your invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <CardTitle className="text-red-600">Invalid Invitation</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-slate-500 mb-4">
              If you believe this is an error, please contact the administrator to request a new invitation.
            </p>
            <Link to="/login">
              <Button variant="outline">Go to Login</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Completion state
  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-amber-700">Account Setup Complete</CardTitle>
            <CardDescription>Your account is pending approval</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                An administrator will review your account shortly. You'll receive an email once your account has been approved.
              </p>
            </div>
            <p className="text-sm text-slate-500">
              Once approved, you can log in using your email address: <strong>{invitation?.email}</strong>
            </p>
            <Link to="/login">
              <Button className="bg-navy-600 hover:bg-navy-700 w-full" style={{ backgroundColor: '#000053' }}>
                Go to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Setup form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg" data-testid="setup-account-page">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#000053' }}>
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl" style={{ color: '#000053' }}>
            Welcome to Polar iHealth
          </CardTitle>
          <CardDescription>Complete your account setup</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">You're being invited as</p>
                <p className="font-medium text-slate-900">{invitation?.email}</p>
              </div>
              <Badge 
                className="capitalize"
                style={{ backgroundColor: invitation?.role === 'physician' ? '#006D73' : '#3b82f6', color: 'white' }}
              >
                {invitation?.role}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className={formErrors.first_name ? 'border-red-500' : ''}
                  data-testid="setup-first-name"
                />
                {formErrors.first_name && (
                  <p className="text-xs text-red-500">{formErrors.first_name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className={formErrors.last_name ? 'border-red-500' : ''}
                  data-testid="setup-last-name"
                />
                {formErrors.last_name && (
                  <p className="text-xs text-red-500">{formErrors.last_name}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Phone Number *</Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="(555) 123-4567"
                value={formData.phone_number}
                onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                className={formErrors.phone_number ? 'border-red-500' : ''}
                data-testid="setup-phone"
              />
              {formErrors.phone_number && (
                <p className="text-xs text-red-500">{formErrors.phone_number}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="alternate_email">Alternate Email (for MFA) *</Label>
              <Input
                id="alternate_email"
                type="email"
                placeholder="Your personal email for verification codes"
                value={formData.alternate_email}
                onChange={(e) => setFormData({ ...formData, alternate_email: e.target.value })}
                className={formErrors.alternate_email ? 'border-red-500' : ''}
                data-testid="setup-alternate-email"
              />
              {formErrors.alternate_email && (
                <p className="text-xs text-red-500">{formErrors.alternate_email}</p>
              )}
              <p className="text-xs text-slate-500">
                Login verification codes will be sent to this email
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <Input
                id="password"
                type="password"
                placeholder="Minimum 8 characters"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className={formErrors.password ? 'border-red-500' : ''}
                data-testid="setup-password"
              />
              {formErrors.password && (
                <p className="text-xs text-red-500">{formErrors.password}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password *</Label>
              <Input
                id="confirm_password"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirm_password}
                onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                className={formErrors.confirm_password ? 'border-red-500' : ''}
                data-testid="setup-confirm-password"
              />
              {formErrors.confirm_password && (
                <p className="text-xs text-red-500">{formErrors.confirm_password}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full"
              style={{ backgroundColor: '#006D73' }}
              disabled={submitting}
              data-testid="setup-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Setting up account...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              After setup, your account will be reviewed by an administrator before you can log in.
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
